import { useState } from 'react';
import { Pencil, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button, BrandLoading } from '@scripsteam/scrips-signal-ds';
import {
  usePractitionerById,
  useProviderBillingSettings,
  useSaveProviderBillingSettings,
} from '@/settings/services';
import { BillingProfileEditDialog } from '../billing/billing-profile-edit-dialog';
import type {
  BillingProfile,
  ProviderBillingSettings,
} from '@/types/settings-api';
import { SETTINGS_SECTION_TITLE } from '@/settings/components/settings-look';

/**
 * Practitioner Billing tab — PROD-763 (complete: PR-A + PR-B + PR-C).
 *
 * Flutter parity: `doctor_billing_tab.dart` + `billing_profiles_list_widget.dart`.
 * Three sections:
 *
 *   1. **Fees summary card** — "[Practitioner]'s Fees" + "N services" +
 *      View button linking to the existing org-wide Provider Fees
 *      detail page (`/billing/master-data/provider-fees/:providerId`).
 *
 *   2. **Billing Profiles list** — per-profile card with name + service
 *      count + Edit affordance. Filters out `isArchived: true` profiles.
 *
 *   3. **Add Billing Profile** — button below the list that opens the
 *      edit dialog with a blank draft.
 *
 * Edit / Add / Archive flows (parent state owns the mutation):
 *   - Edit / Add open the same `BillingProfileEditDialog`. The dialog
 *     handles name + service add/remove/delete internally (PR-C). Save
 *     returns the modified `BillingProfile`; parent splices it into a
 *     fresh `billingProfiles[]` (replace by id on edit, append on add)
 *     and dispatches `useSaveProviderBillingSettings` with the full
 *     envelope. Backend echoes back canonical state which lands directly
 *     in the cache via `setQueryData` (mutation hook handles it).
 *   - Archive flips `isDeleted: true` + `isArchived: true` on the target
 *     profile and dispatches the same save. Confirmation gate is in the
 *     dialog (two-tap pattern per @samer PR #124 review).
 *
 * Visibility gated by `Billing_Settings` permission in the shell
 * (Flutter `doctor_base_widget.dart:230-242`). Tab itself does not
 * check permissions.
 *
 * Wire-shape preserved (verified PROD-763 cURLs across all three PRs):
 *   - `servicesCount` is the org-wide count for the practitioner, not
 *     the sum of profile.services.length. Display the top-level value.
 *   - `id: null` on a new profile / service / visitFee survives
 *     JSON.stringify; backend allocates the join PK server-side.
 *   - `isArchived: true` profiles still ship on the GET; we filter them
 *     out for display. Mutation signals (`isDeleted` / `isUpdated`)
 *     are read-surface-forbidden — see guard on the filter.
 */
export interface PractitionerBillingTabProps {
  doctorSetupId: string;
}

export function PractitionerBillingTab({
  doctorSetupId,
}: PractitionerBillingTabProps) {
  // Cross-tab cache dependency (per @samer PR #132 review):
  //   This call hits the SAME query key as the Personal tab's
  //   `usePractitionerById` — React Query dedupes, so the Personal tab
  //   populating the cache on its first render avoids a second GET when
  //   the user navigates here. If the Personal tab hasn't loaded yet
  //   (cold cache — e.g. direct deep link to the Billing tab) the hook
  //   fires its own GET silently.
  const { data: practitioner } = usePractitionerById(doctorSetupId);
  const practitionerName = practitioner
    ? `${practitioner.name?.firstName ?? ''} ${practitioner.name?.lastName ?? ''}`.trim() || undefined
    : undefined;

  const { data, isLoading, error } = useProviderBillingSettings(doctorSetupId);
  const saveMutation = useSaveProviderBillingSettings();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<BillingProfile | null>(
    null,
  );

  const handleAddClick = () => {
    setEditingProfile(makeEmptyProfile(doctorSetupId));
    setDialogOpen(true);
  };

  const handleEditClick = (profile: BillingProfile) => {
    setEditingProfile(profile);
    setDialogOpen(true);
  };

  const handleSave = (next: BillingProfile) => {
    if (!data) return;
    const existing = data.billingProfiles ?? [];

    // Replace by id on edit; append on add (when id is null).
    const updated: BillingProfile[] =
      next.id !== null
        ? existing.map((p) => (p.id === next.id ? next : p))
        : [...existing, next];

    saveMutation.mutate(
      { ...data, billingProfiles: updated },
      {
        onSuccess: () => {
          toast.success(
            next.id ? 'Billing profile updated' : 'Billing profile added',
          );
        },
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Save failed'),
      },
    );
  };

  const handleArchive = () => {
    if (!data || !editingProfile || !editingProfile.id) return;
    // Flutter archive flow (doctor_billing_tab.dart:117-131):
    //   widget.providerBillingSettings.billingProfiles[idx].isDeleted = true;
    //   widget.providerBillingSettings.billingProfiles[idx].isArchived = true;
    //   bloc.add(UpdateProviderBillingSettingsEvent(...));
    // Both flags are set, then a normal save is dispatched.
    //
    // **Flag scope (per @samer PR #136 review item 4):**
    //   `isArchived` exists at THREE nesting levels on the wire:
    //     1. `BillingProfile.isArchived`        ← archive flag for the PROFILE
    //     2. `visitFees[].isArchived`            ← archive flag for the FEE row
    //     3. `visitFees[].location.isArchived`   ← archive flag for the LOCATION
    //   When archiving a profile we ONLY flip the profile-level flags
    //   (isDeleted + isArchived). The nested visitFees and location
    //   archive flags are part of the unchanged service / location
    //   state and are preserved verbatim by the `{ ...p, ... }` spread —
    //   no recursion into the nested arrays. Touching them would
    //   archive every fee row and every location, which is NOT what
    //   the Flutter flow does. Confirmed against the live save cURL.
    const archived: BillingProfile[] = (data.billingProfiles ?? []).map((p) =>
      p.id === editingProfile.id
        ? { ...p, isDeleted: true, isArchived: true }
        : p,
    );

    saveMutation.mutate(
      { ...data, billingProfiles: archived },
      {
        onSuccess: () => toast.success('Billing profile archived'),
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Archive failed'),
      },
    );
  };

  if (isLoading) {
    return (
      <BrandLoading size={48} label="Loading billing settings…" />
    );
  }

  if (error) {
    return (
      <p className="text-sm text-status-error">
        Failed to load billing settings. Please try again.
      </p>
    );
  }

  if (!data) {
    return (
      <p className="text-sm text-text-muted">
        No billing settings available for this practitioner.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <FeesSummaryCard
        settings={data}
        practitionerName={practitionerName}
        doctorSetupId={doctorSetupId}
      />

      <BillingProfilesList
        profiles={data.billingProfiles ?? []}
        onAdd={handleAddClick}
        onEdit={handleEditClick}
        isSaving={saveMutation.isPending}
      />

      {editingProfile && (
        <BillingProfileEditDialog
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) setEditingProfile(null);
          }}
          initial={editingProfile}
          onSave={handleSave}
          onArchive={editingProfile.id ? handleArchive : undefined}
        />
      )}
    </div>
  );
}

function makeEmptyProfile(providerId: string): BillingProfile {
  return {
    isProviderFee: false,
    providerId,
    id: null,
    name: '',
    isUpdated: false,
    isDeleted: false,
    services: [],
    totalCost: 0,
    isArchived: false,
    createdOn: null,
    createdById: null,
    updatedOn: null,
    updatedById: null,
  };
}

function FeesSummaryCard({
  settings,
  practitionerName,
  doctorSetupId,
}: {
  settings: ProviderBillingSettings;
  practitionerName?: string;
  doctorSetupId: string;
}) {
  const navigate = useNavigate();
  const count = settings.servicesCount ?? 0;
  const label = `${practitionerName ? `${practitionerName}'s` : 'Practitioner'} Fees`;

  return (
    <section className="rounded-lg border border-border-hairline bg-surface-card shadow-1 px-4 py-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h2 className="text-base font-bold text-text-default">{label}</h2>
          <p className="mt-1 text-sm text-text-muted">
            {count} {count === 1 ? 'service' : 'services'}
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={() =>
            navigate(`/billing/master-data/provider-fees/${doctorSetupId}`)
          }
        >
          View
        </Button>
      </div>
    </section>
  );
}

function BillingProfilesList({
  profiles,
  onAdd,
  onEdit,
  isSaving,
}: {
  profiles: BillingProfile[];
  onAdd: () => void;
  onEdit: (profile: BillingProfile) => void;
  isSaving: boolean;
}) {
  // Filter on `isArchived` ONLY. Per @samer PR #132 review:
  //
  //   `isDeleted` and `isUpdated` on `BillingProfile` ship as `null` on
  //   GET — they're WRITE-TIME flags used during the archive / save
  //   mutation (Flutter `doctor_billing_tab.dart:121-129` sets
  //   `isDeleted: true` when the user confirms archive, then POSTs).
  //   Reading them on this surface — or filtering them into / out of
  //   the visible list — is a CORRECTNESS BUG waiting to land. The
  //   only stable archive-state flag on read is `isArchived: boolean`
  //   (non-null per the verified GET response).
  //
  //   DO NOT add `&& !p.isDeleted` here. DO NOT branch on `isUpdated`.
  //   Those are mutation signals the server consumes; they have no
  //   meaning on the read surface.
  const visible = profiles.filter((p) => !p.isArchived);

  return (
    <section className="rounded-lg border border-border-hairline bg-surface-card shadow-1 px-4 py-4">
      <h2 className={SETTINGS_SECTION_TITLE}>
        Billing profiles
      </h2>

      {visible.length === 0 ? (
        <p className="mt-3 text-sm text-text-muted">
          No billing profiles configured.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-border-hairline">
          {visible.map((profile) => (
            <li
              key={profile.id ?? profile.name}
              className="flex items-start justify-between gap-2 py-3"
            >
              <div className="flex-1">
                <p className="text-base font-bold text-text-default">
                  {profile.name?.trim() || 'Unnamed profile'}
                </p>
                <p className="mt-1 text-sm text-text-muted">
                  {profile.services?.length ?? 0}{' '}
                  {(profile.services?.length ?? 0) === 1 ? 'service' : 'services'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => onEdit(profile)}
                disabled={isSaving}
                aria-label={`Edit ${profile.name || 'billing profile'}`}
                className="icon-action"
              >
                <Pencil className="size-4" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 border-t border-border-hairline pt-3">
        <Button
          type="button"
          variant="tertiary"
          onClick={onAdd}
          disabled={isSaving}
          className="text-brand-primary"
        >
          <Plus className="size-4 mr-2" />
          {visible.length === 0
            ? 'Add Billing Profile'
            : 'Add Another Billing Profile'}
        </Button>
      </div>
    </section>
  );
}
